import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { apiGet, apiPatch, AuthError, apiPost } from '../api';
import { useClient } from '../ClientContext';
import Icon from '../components/Icon';

/**
 * Approval Queue.
 *
 * The old page put Approve and Reject on the collapsed row, so a decision
 * could be recorded without ever opening the item — without seeing the
 * instruction that produced it or the rule that caught it. That is backwards
 * for a page whose entire job is to make someone look before they decide.
 *
 * So: the queue on the left, the case on the right, and the decision at the
 * bottom of the case, where you arrive only after reading it.
 *
 * Rejecting requires a note. The endpoint has always accepted one — it
 * validates the length and stores it against the decision — the frontend just
 * never sent it, which left every rejection on the audit record with no reason
 * attached.
 *
 * There is deliberately no bulk approve. Approving twelve governance
 * exceptions in one click is the behaviour this product exists to prevent.
 */

const money = (n) => `£${(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

const RULE_LABEL = {
  daily_budget_ceiling_gbp:  'Daily budget ceiling',
  review_threshold_gbp:      'Review threshold',
  creative_must_be_approved: 'Creative must be approved',
  audience_floor_size:       'Minimum audience size',
  objective_creative_match:  'Creative matches objective',
  no_weekend_launch:         'No weekend launches',
  require_exclusion_list:    'Require exclusion list',
  duplicate_detection_days:  'Duplicate window',
};

const ageHours = (iso) => {
  if (!iso) return 0;
  const d = new Date(iso);
  return isNaN(d) ? 0 : (Date.now() - d.getTime()) / 36e5;
};

const when = (iso) => {
  const h = ageHours(iso);
  if (!iso) return '';
  if (h < 1) return 'just now';
  if (h < 24) return `${Math.round(h)}h ago`;
  if (h < 48) return 'yesterday';
  return `${Math.round(h / 24)}d ago`;
};

export default function ApprovalQueue() {
  const { clientParam, isAll } = useClient();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [sort, setSort] = useState('value');
  const [note, setNote] = useState('');
  const [noteNeeded, setNoteNeeded] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [leaving, setLeaving] = useState(null);

  const noteRef = useRef(null);

  const load = useCallback(() => {
    apiGet(`/approval-queue${clientParam}`)
      .then((data) => {
        setItems(data.items || []);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof AuthError ? err.message : err.message || 'Could not load the approval queue.');
        setLoading(false);
      });
  }, [clientParam]);

  useEffect(() => { setLoading(true); setSelectedId(null); load(); }, [load]);

  const sorted = useMemo(() => {
    const list = items.slice();
    if (sort === 'value') list.sort((a, b) => (b.spend_value || 0) - (a.spend_value || 0));
    else if (sort === 'age') list.sort((a, b) => ageHours(b.created_at) - ageHours(a.created_at));
    else list.sort((a, b) =>
      String(a.rule_triggered || '').localeCompare(String(b.rule_triggered || '')) ||
      (b.spend_value || 0) - (a.spend_value || 0));
    return list;
  }, [items, sort]);

  // Keep a selection valid as the queue empties beneath it.
  useEffect(() => {
    if (!sorted.length) { setSelectedId(null); return; }
    if (!sorted.some((i) => i.approval_id === selectedId)) {
      setSelectedId(sorted[0].approval_id);
    }
  }, [sorted, selectedId]);

  useEffect(() => { setNote(''); setNoteNeeded(false); }, [selectedId]);

  const current = sorted.find((i) => i.approval_id === selectedId) || null;
  const atRisk = items.reduce((sum, i) => sum + (i.spend_value || 0), 0);

  const decide = useCallback(async (status) => {
    if (!current || deciding) return;

    if (status === 'rejected' && !note.trim()) {
      setNoteNeeded(true);
      noteRef.current?.focus();
      return;
    }

    setDeciding(true);
    setNotice(null);
    const id = current.approval_id;

    try {
      await apiPatch(`/approvals/${id}`, {
        status,
        note: note.trim() || undefined,
      });

      // Recording the decision and building the campaign are separate calls.
      // Approving here does both, because "approve and release" is what the
      // button says and a queue that agrees without acting is the dead end
      // this page had.
      let message = 'Decision recorded.';

      if (status === 'approved') {
        // Which platform governance evaluated this under travels on the
        // stored action; the server decides from the decision record, this
        // only words the notice.
        const google = current.proposed_action?.platform === 'google';
        const where = google ? 'Google Ads' : 'Meta';
        const activateIn = google ? 'Google Ads' : 'Ads Manager';
        try {
          const built = await apiPost(`/approvals/${id}/execute`);
          message = built.already_executed
            ? `Already on ${where} as campaign ${built.campaign_id}.`
            : `Created on ${where} and paused — campaign ${built.campaign_id}. Enable it in ${activateIn}.`;
        } catch (err) {
          // The approval stands. A platform failure is not a reason to discard
          // a human judgement, and the two states are genuinely different:
          // approved-but-not-built is recoverable, un-approved is not.
          message =
            `Approved, but ${where} did not accept it: ${err.message || 'unknown error'}. ` +
            `The approval is recorded and can be retried.`;
        }
      } else {
        message = 'Rejected. Nothing was created.';
      }

      setLeaving(id);
      setTimeout(() => {
        setItems((list) => list.filter((i) => i.approval_id !== id));
        setLeaving(null);
      }, 240);

      setNotice(message);
    } catch (err) {
      setNotice(err.message || 'Could not record that decision.');
    }
    setDeciding(false);
  }, [current, deciding, note]);

  // Triage keys. Ignored while typing, so the note field still works normally.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (!sorted.length) return;
      const i = sorted.findIndex((x) => x.approval_id === selectedId);

      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedId(sorted[Math.min(sorted.length - 1, i + 1)]?.approval_id);
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedId(sorted[Math.max(0, i - 1)]?.approval_id);
      } else if (e.key === 'a') {
        e.preventDefault(); decide('approved');
      } else if (e.key === 'r') {
        e.preventDefault(); decide('rejected');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sorted, selectedId, decide]);

  if (loading) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Approval Queue</h1>
          <p className="page-subtitle">Loading what is waiting…</p>
        </div>
        <div className="skeleton" style={{ height: 560, borderRadius: 'var(--radius-md)' }} />
      </>
    );
  }

  const proposed = current?.proposed_action || {};

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Approval Queue</h1>
        <p className="page-subtitle">
          Actions an agent proposed that a governance rule held back. Read the brief and the
          rule before you decide — that is what the queue is for.
        </p>
      </div>

      {error && (
        <div className="banner bad">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className="banner ok">
          <Icon name="check" size={16} strokeWidth={2.6} />
          <span>{notice}</span>
        </div>
      )}

      {/* About the items waiting below - so only shown while there are some.
          With the queue empty it read as a status, contradicting the
          "Created on Google Ads" notice right above it. */}
      {items.length > 0 && <div className="banner info">
        <Icon name="info" size={16} strokeWidth={2.2} />
        <span>
          Nothing waiting here has reached the ad platform yet. Approving creates the campaign on the
          platform it was proposed for — Meta or Google Ads — paused, ready for you to enable.
          Rejecting records your decision and creates nothing.
        </span>
      </div>}

      {items.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>All clear</h3>
            <p>Nothing is waiting for review.</p>
          </div>
        </div>
      ) : (
        <div className="review">
          <div className="rv-list">
            <div className="rv-listhead">
              <span className="card-title">{items.length} pending</span>
              <span className="card-note">{money(atRisk)}/day held</span>
              <div className="seg" role="group" aria-label="Sort the queue" style={{ width: '100%', marginTop: 4 }}>
                {[['value', 'Value'], ['age', 'Oldest'], ['rule', 'Rule']].map(([key, label]) => (
                  <button
                    key={key}
                    style={{ flex: 1 }}
                    aria-pressed={sort === key}
                    onClick={() => setSort(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="rv-items">
              {sorted.map((item) => (
                <button
                  key={item.approval_id}
                  className={`rv-item ${item.approval_id === selectedId ? 'sel' : ''} ${leaving === item.approval_id ? 'gone' : ''}`}
                  onClick={() => setSelectedId(item.approval_id)}
                >
                  <div className="rv-top">
                    <span className="rv-name">
                      {item.campaign_name || item.proposed_action?.name || 'Unnamed campaign'}
                    </span>
                    <span className="rv-val">{money(item.spend_value)}</span>
                  </div>
                  <div className="rv-sub">
                    <span className="tag-mono">{item.rule_triggered || '—'}</span>
                    <span className="rv-age">{when(item.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="rv-detail">
            {!current ? (
              <div className="empty" style={{ margin: 'auto' }}>
                <h3>Nothing selected</h3>
                <p>Pick an item from the queue.</p>
              </div>
            ) : (
              <>
                <div className="rv-head">
                  <div className="rv-h1">
                    {current.campaign_name || proposed.name || 'Unnamed campaign'}
                  </div>
                  <div className="rv-meta">
                    <span className="pill flagged">
                      <Icon name="alert" size={12} strokeWidth={2.4} /> Flagged
                    </span>
                    {isAll && current.client_name && <><span>{current.client_name}</span><span>·</span></>}
                    <span>{when(current.created_at)}</span>
                    {current.action_id && <><span>·</span><span className="tag-mono">{current.action_id}</span></>}
                  </div>
                </div>

                <div className="rv-body">
                  <div className="rv-block">
                    <h4>What was asked for</h4>
                    <div className="rv-quote">
                      {current.instruction
                        ? `“${current.instruction}”`
                        : <span style={{ color: 'var(--text-muted)' }}>
                            No instruction recorded, or it has passed its 180-day retention period.
                          </span>}
                    </div>
                  </div>

                  <div className="rv-block">
                    <h4>Why it was held</h4>
                    <div className="rv-why">
                      <Icon name="shield" size={16} strokeWidth={2.2} />
                      <span>
                        <b>{RULE_LABEL[current.rule_triggered] || current.rule_triggered || 'A governance rule'}</b>
                        {current.reason ? ` — ${current.reason}` : ''}
                        {current.rule_triggered && (
                          <span className="tag-mono" style={{ marginLeft: 8 }}>{current.rule_triggered}</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {Object.keys(proposed).length > 0 && (
                    <div className="rv-block">
                      <h4>What the agent proposed</h4>
                      <div className="rv-spec">
                        {proposed.objective && (
                          <div><span className="k">Objective</span><span className="v">{proposed.objective}</span></div>
                        )}
                        {proposed.daily_budget_pence != null && (
                          <div><span className="k">Daily budget</span><span className="v">{money(proposed.daily_budget_pence / 100)}</span></div>
                        )}
                        {proposed.estimated_audience_size != null && (
                          <div><span className="k">Est. audience</span><span className="v">{Number(proposed.estimated_audience_size).toLocaleString('en-GB')}</span></div>
                        )}
                        {proposed.platform === 'google' && (
                          <div><span className="k">Platform</span><span className="v">Google Ads · Search</span></div>
                        )}
                        {proposed.bidding_strategy && (
                          <div><span className="k">Bidding</span><span className="v">{proposed.bidding_strategy}</span></div>
                        )}
                        {Array.isArray(proposed.keywords) && proposed.keywords.length > 0 && (
                          <div><span className="k">Keywords</span><span className="v">
                            {proposed.keywords.map((k) => `${k.text} (${(k.match_type || '').toLowerCase()})`).join(', ')}
                          </span></div>
                        )}
                        {proposed.final_url && (
                          <div><span className="k">Final URL</span><span className="v">{proposed.final_url}</span></div>
                        )}
                        {proposed.creative_name && (
                          <div><span className="k">Creative</span><span className="v">{proposed.creative_name}</span></div>
                        )}
                        <div><span className="k">Status if approved</span><span className="v">Paused</span></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rv-foot">
                  <textarea
                    ref={noteRef}
                    rows={2}
                    className={`rv-note ${noteNeeded ? 'needed' : ''}`}
                    value={note}
                    onChange={(e) => { setNote(e.target.value); if (e.target.value.trim()) setNoteNeeded(false); }}
                    placeholder={noteNeeded
                      ? 'A rejection needs a reason — it goes on the audit record.'
                      : 'Add a note. It is stored with your decision (optional to approve, required to reject).'}
                    aria-label="Note to record with your decision"
                  />
                  <div className="rv-actions">
                    <button className="btn btn-approve" disabled={deciding} onClick={() => decide('approved')}>
                      <Icon name="check" size={14} strokeWidth={2.6} />
                      {deciding ? 'Creating…' : 'Approve and release'}
                    </button>
                    <button className="btn btn-reject" disabled={deciding} onClick={() => decide('rejected')}>
                      <Icon name="x" size={14} strokeWidth={2.6} /> Reject
                    </button>
                    <span className="rv-kbd">
                      <kbd>J</kbd><kbd>K</kbd> move · <kbd>A</kbd> approve · <kbd>R</kbd> reject
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}